import { create } from "zustand";
import type { BackgroundService, BackgroundServiceRegistration } from "../types";

interface BackgroundServiceManagerState {
  services: Map<string, BackgroundService>;
  register: (registration: BackgroundServiceRegistration) => Promise<void>;
  unregister: (serviceId: string) => Promise<void>;
  getService: (serviceId: string) => BackgroundService | undefined;
  getServicesByApp: (appId: string) => BackgroundService[];
  getAllServices: () => BackgroundService[];
}

export const useBackgroundServiceManager =
  create<BackgroundServiceManagerState>((set, get) => ({
    services: new Map(),

    register: async (registration) => {
      const existing = get().services.get(registration.id);
      if (existing) return;

      const service: BackgroundService = {
        id: registration.id,
        appId: registration.appId,
        persistentUI: registration.persistentUI,
        state: {},
        onInit: registration.onInit,
        onDestroy: registration.onDestroy,
        getState: registration.getState,
      };

      if (service.onInit) {
        await service.onInit();
      }

      set((state) => {
        const services = new Map(state.services);
        services.set(service.id, service);
        return { services };
      });
    },

    unregister: async (serviceId) => {
      const service = get().services.get(serviceId);
      if (!service) return;

      if (service.onDestroy) {
        await service.onDestroy();
      }

      set((state) => {
        const services = new Map(state.services);
        services.delete(serviceId);
        return { services };
      });
    },

    getService: (serviceId) => get().services.get(serviceId),

    getServicesByApp: (appId) =>
      Array.from(get().services.values()).filter((s) => s.appId === appId),

    getAllServices: () => Array.from(get().services.values()),
  }));
